import * as React from 'react';
import type { ActionBlock, AskBlock, MessageBlock, NoticeBlock, TextBlock } from '@/types/api';
import { isExpertBlockType } from '@/types/api';
import { ACTOR_KIND, TOOL_FAMILY } from '@/lib/copy';
import { cn } from '@/lib/utils';
import { Markdown } from '@/lib/markdown/markdown';
import { UnknownTurn } from '@/components/unknown-turn';

/* ---------------------------------------------------------------------------
   Message block renderers.

   `action` IS in this dashboard's block set, unlike the client's — the expert is
   inside the trust boundary and the tool stream is the point of the surface. But
   the set is still an ALLOWLIST: a block type this build has no mapping for goes
   to `<UnknownTurn>` rather than being drawn optimistically, because drawing it
   would assert something about whether the client can read it, and this renderer
   does not know.

   Text blocks are markdown from an agent that read an untrusted repository. They
   go through the strict parser in lib/markdown, which builds a closed node union
   and never constructs an HTML string — there is no `dangerouslySetInnerHTML`
   anywhere in this app and there must never be one. The access token lives in
   this app's memory and an expert's token can merge to production, which makes a
   prompt-injected repo reaching XSS here the most expensive bug available.
--------------------------------------------------------------------------- */

export function BlockView({
  block,
  onAnswerAsk,
}: {
  block: MessageBlock;
  onAnswerAsk?: (ask: AskBlock) => void;
}) {
  const type = (block as { type?: string }).type;

  if (!type || !isExpertBlockType(type)) {
    return <UnknownTurn issueKind="unknown_block_type" token={String(type ?? 'missing').slice(0, 48)} />;
  }

  switch (block.type) {
    case 'text':
      return <TextBlockView block={block} />;
    case 'ask':
      return <AskBlockView block={block} onAnswer={onAnswerAsk} />;
    case 'action':
      return <ActionBlockView block={block} />;
    case 'notice':
      return <NoticeBlockView block={block} />;
    default: {
      const exhaustive: never = block;
      void exhaustive;
      return null;
    }
  }
}

function TextBlockView({ block }: { block: TextBlock }) {
  return <Markdown source={block.text} />;
}

/**
 * A parked question.
 *
 * Who may answer is decided by the chat's status, not by who is connected: an
 * ask raised in `draft` is the client's even if an expert is watching, and an ask
 * raised during review is the expert's. So the answer control is offered by the
 * thread, which knows the status, and this component only renders what the ask
 * is and what became of it.
 */
function AskBlockView({ block, onAnswer }: { block: AskBlock; onAnswer?: (ask: AskBlock) => void }) {
  const resolution = block.resolution ?? (block.answered ? 'answered' : null);

  return (
    <div className="border-l-2 border-warn bg-warn-soft px-2.5 py-2">
      <p className="eyebrow text-warn">{resolution ? 'Question resolved' : 'Question parked'}</p>
      <p className="mt-1 text-sm leading-[19px] text-ink">{block.question}</p>

      {block.options && block.options.length > 0 ? (
        <ul className="m-0 mt-1.5 flex list-none flex-wrap gap-1.5 p-0">
          {block.options.map((option) => (
            <li
              key={option.value}
              className={cn(
                'border px-1.5 py-px font-mono text-tiny',
                block.answer === option.value ? 'border-ok text-ok' : 'border-rule-strong text-ink-3',
              )}
            >
              {option.label}
            </li>
          ))}
        </ul>
      ) : null}

      {resolution ? (
        <p className="mt-1.5 font-mono text-tiny text-ink-2">
          {resolution === 'answered'
            ? `answered by ${block.answered_by_kind ? ACTOR_KIND[block.answered_by_kind].toLowerCase() : 'someone'}`
            : resolution === 'denied_timeout'
              ? 'denied on timeout — the platform answered with an explanation the agent could act on, rather than killing the run'
              : resolution === 'auto_approved'
                ? 'auto-approved by the policy table'
                : 'superseded'}
        </p>
      ) : null}

      {block.answer ? <p className="mt-1 text-xs leading-[17px] text-ink-2">“{block.answer}”</p> : null}

      {!resolution && onAnswer ? (
        <button
          type="button"
          onClick={() => onAnswer(block)}
          className="mt-2 font-mono text-tiny uppercase tracking-[0.08em] text-action underline underline-offset-2"
        >
          Answer this
        </button>
      ) : null}
    </div>
  );
}

const ACTION_STATUS_TONE = {
  running: 'text-live',
  succeeded: 'text-ok',
  failed: 'text-danger',
  denied: 'text-warn',
} as const;

/**
 * A tool call.
 *
 * Switched on `family`. `vendor_name` is rendered as an inert label and never
 * branched on, so a different agent's tool set can only add strings inside
 * `other` rather than break this renderer (invariant I10).
 *
 * The detail is collapsed by default: it is truncated server-side at 8KB, is
 * display-only, and is never replayed into an agent — so nothing depends on it
 * being read, and expanding forty of them by default would bury the thread.
 */
function ActionBlockView({ block }: { block: ActionBlock }) {
  const [open, setOpen] = React.useState(false);
  const family = TOOL_FAMILY[block.family];
  const status = block.status ?? 'succeeded';

  return (
    <div className="border border-rule bg-raised">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!block.detail}
        className="flex w-full items-baseline gap-2 px-2 py-1 text-left disabled:cursor-default"
      >
        <span className="shrink-0 font-mono text-tiny uppercase tracking-[0.08em] text-ink-3" title={family.detail}>
          {family.label}
        </span>
        <span className={cn('shrink-0 font-mono text-tiny', ACTION_STATUS_TONE[status])}>{status}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-ink-2">{block.summary ?? family.detail}</span>
        {block.vendor_name ? (
          <span className="shrink-0 font-mono text-tiny text-ink-3" title="Display label only — never switched on">
            {block.vendor_name}
          </span>
        ) : null}
        {block.detail ? (
          <span aria-hidden className="shrink-0 font-mono text-tiny text-ink-3">
            {open ? '−' : '+'}
          </span>
        ) : null}
      </button>

      {block.paths && block.paths.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-0.5 border-t border-rule px-2 py-1 p-0">
          {block.paths.slice(0, 8).map((path) => (
            <li key={path} className="break-all font-mono text-tiny text-ink-3">
              {path}
            </li>
          ))}
          {block.paths.length > 8 ? (
            <li className="font-mono text-tiny text-ink-3">+{block.paths.length - 8} more</li>
          ) : null}
        </ul>
      ) : null}

      {open && block.detail ? (
        <div className="border-t border-rule bg-sunken">
          <pre className="m-0 max-h-72 overflow-auto px-2 py-1.5 font-mono text-tiny leading-[16px] text-ink-2">
            {block.detail}
          </pre>
          {block.detail_truncated ? (
            <p className="border-t border-rule px-2 py-1 font-mono text-tiny text-ink-3">
              truncated server-side at 8KB — display only, never replayed into an agent
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NoticeBlockView({ block }: { block: NoticeBlock }) {
  return (
    <div
      className={cn(
        'border-l-2 px-2.5 py-1.5',
        block.level === 'error' && 'border-danger bg-danger-soft',
        block.level === 'warning' && 'border-warn bg-warn-soft',
        block.level === 'info' && 'border-rule-strong bg-sunken',
      )}
    >
      <p className="flex flex-wrap items-baseline gap-x-2 text-xs leading-[17px] text-ink">
        {block.message}
        {block.error_code ? <span className="font-mono text-tiny text-ink-3">{block.error_code}</span> : null}
      </p>
    </div>
  );
}
